import { useCallback, useEffect, useState } from 'react';
import { notify, type UnifiedNotificationRequest, type UnifiedNotificationResult } from '@/services/unifiedNotificationOrchestratorService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

/**
 * Hook for sending unified notifications
 * 
 * This hook provides a simple interface for sending notifications that will
 * automatically be delivered through appropriate channels based on delivery rules
 * and user preferences.
 * 
 * @example
 * ```typescript
 * const { notifyUser, notifyEmail } = useUnifiedNotifications();
 * 
 * // Notify a registered user
 * await notifyUser('application_received', userId, {
 *   job_title: 'Software Engineer',
 *   company_name: 'Tech Corp'
 * });
 * 
 * // Notify via email (for guest users)
 * await notifyEmail('application_submitted', 'guest@example.com', {
 *   job_title: 'Software Engineer'
 * });
 * ```
 */
export function useUnifiedNotifications() {
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
  }, []);

  /**
   * Send notification to a registered user by user ID
   */
  const notifyUser = useCallback(async (
    notificationType: string,
    recipientUserId: string,
    templateVariables?: Record<string, any>,
    urgency: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    context?: Record<string, any>
  ): Promise<UnifiedNotificationResult> => {
    return notify({
      notificationType,
      recipientUserId,
      templateVariables,
      urgency,
      context
    });
  }, []);

  /**
   * Send notification to an email address (for guest users or external recipients)
   */
  const notifyEmail = useCallback(async (
    notificationType: string,
    recipientEmail: string,
    templateVariables?: Record<string, any>,
    urgency: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    context?: Record<string, any>
  ): Promise<UnifiedNotificationResult> => {
    return notify({
      notificationType,
      recipientEmail,
      templateVariables,
      urgency,
      context
    });
  }, []);

  /**
   * Send notification to the current authenticated user
   */
  const notifyCurrentUser = useCallback(async (
    notificationType: string,
    templateVariables?: Record<string, any>,
    urgency: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    context?: Record<string, any>
  ): Promise<UnifiedNotificationResult> => {
    if (!user?.id) {
      throw new Error('No authenticated user found');
    }

    return notify({
      notificationType,
      recipientUserId: user.id,
      templateVariables,
      urgency,
      context
    });
  }, [user?.id]);

  /**
   * Send a custom notification with full control over all parameters
   */
  const notifyCustom = useCallback(async (
    request: UnifiedNotificationRequest
  ): Promise<UnifiedNotificationResult> => {
    return notify(request);
  }, []);

  /**
   * Show error notification locally only (bypasses unified system)
   * Use this for error messages you don't want to send via email/in-app
   */
  const notifyErrorLocal = useCallback((
    title: string,
    message: string,
    options?: { duration?: number; action?: { label: string; onClick: () => void } }
  ) => {
    toast.error(title, {
      description: message,
      duration: options?.duration || 4000,
      ...(options?.action && { 
        action: {
          label: options.action.label,
          onClick: options.action.onClick
        }
      })
    });
  }, []);


  /**
   * Convenience methods for essential notification types
   */
  const notifyApplicationReceived = useCallback(async (
    recipientUserId: string,
    jobTitle: string,
    candidateName: string,
    applicationReviewUrl: string,
    applicationTime?: string,
    candidateProfileUrl?: string
  ): Promise<UnifiedNotificationResult> => {
    return notifyUser(
      'candidate_application_received',
      recipientUserId,
      {
        job_title: jobTitle,
        candidate_name: candidateName,
        application_review_url: applicationReviewUrl,
        application_time: applicationTime || new Date().toLocaleString(),
        candidate_profile_url: candidateProfileUrl
      },
      'medium'
    );
  }, [notifyUser]);

  // REMOVED: notifyApplicationSubmitted - Low value notification (submission_confirmed disabled)
  // Users get confirmation through application status changes instead

  const notifyCompanyInvitation = useCallback(async (
    recipientUserId: string,
    companyName: string,
    role: string,
    inviterName: string
  ): Promise<UnifiedNotificationResult> => {
    return notifyUser(
      'company_invitation',
      recipientUserId,
      {
        company_name: companyName,
        role,
        inviter_name: inviterName
      },
      'high'
    );
  }, [notifyUser]);

  // REMOVED: notifyInterviewScheduled - Replaced by detailed interview_booking_confirmed notifications
  // sent directly by the process-interview-booking Edge Function to avoid duplicates

  const notifySystemMaintenance = useCallback(async (
    recipientUserId: string,
    message: string,
    maintenanceTime?: string
  ): Promise<UnifiedNotificationResult> => {
    return notifyUser(
      'system_maintenance',
      recipientUserId,
      {
        message,
        maintenance_time: maintenanceTime
      },
      'critical'
    );
  }, [notifyUser]);

  const notifyStageAssigned = useCallback(async (
    recipientUserId: string,
    jobTitle: string,
    companyName: string,
    stageName: string,
    applicationUrl: string,
    stageDescription?: string,
    deadline?: string
  ): Promise<UnifiedNotificationResult> => {
    return notifyUser(
      'stage_assigned',
      recipientUserId,
      {
        job_title: jobTitle,
        company_name: companyName,
        candidate_name: 'You',
        stage_name: stageName,
        application_url: applicationUrl,
        stage_description: stageDescription,
        deadline: deadline
      },
      'high'
    );
  }, [notifyUser]);

  const notifySubmissionReady = useCallback(async (
    recipientUserId: string,
    candidateName: string,
    jobTitle: string,
    stageName: string,
    submissionReviewUrl: string,
    completionTime?: string,
    candidateProfileUrl?: string
  ): Promise<UnifiedNotificationResult> => {
    return notifyUser(
      'candidate_submission_ready',
      recipientUserId,
      {
        candidate_name: candidateName,
        job_title: jobTitle,
        stage_name: stageName,
        submission_review_url: submissionReviewUrl,
        completion_time: completionTime || new Date().toLocaleString(),
        candidate_profile_url: candidateProfileUrl
      },
      'medium'
    );
  }, [notifyUser]);


  return {
    // Core notification methods
    notifyUser,
    notifyEmail,
    notifyCurrentUser,
    notifyCustom,
    notifyErrorLocal,
    
    // Essential notification methods for job and application workflow (optimized)
    notifyApplicationReceived,  // MILESTONE: New applications for reviewers
    // notifyApplicationSubmitted - REMOVED (low value)
    notifyStageAssigned,        // URGENT_ACTION: Tasks requiring user action  
    notifySubmissionReady,      // MILESTONE: Completed submissions for review
    notifyCompanyInvitation,    // INVITATION: Actionable invites
    // notifyInterviewScheduled - REMOVED (replaced by Edge Function notifications)
    notifySystemMaintenance,    // CRITICAL: System notifications
  };
}